import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import { parseTimeString } from "../../../utils/dateUtils";
import { DailyPrintoutGroupingOption } from "../../../utils/models";
import { groupKeyFor } from "../group/groupKey";
import { COMPOSITE_SECTION_KEY_SEPARATOR, UNGROUPED_SECTION_KEY } from "../group/sectionGrouping";
import { groupRowsByDepartmentAnchors } from "../group/shiftTimeDepartmentAnchors";
import {
	bucketRowsByCustomTimeGroup,
	customTimeGroupKeyLabel,
	isCustomTimeGroupKey,
	shiftBelongsToCustomTimeGroupKey,
	UNMATCHED_GROUP_KEY,
} from "../layout/customTimeGroups";
import type { ActiveCustomTimeGroups, CustomSheetTabsConfig, ScheduleRow, SectionPlan, SheetPlan, ShiftTimeAnchor } from "../types";
import { groupingKeyToDisplayName } from "../util/groupingDisplay";
import { shiftWindowKeyToDisplayName } from "../util/shiftTimeFormat";
import { buildCustomTabCandidates } from "./customTabs";
import { finalizeSheetNames, finalizeUserSheetNames } from "./sheetNames";
import { dedupSectionsAfterShiftMerge, mergeOverlappingShiftSheets, type SheetBucketMap } from "./timeOverlapMerge";

const MINUTES_IN_DAY = 1440;

/**
 * Minute-offset of a shift's start time relative to the department's day start, wrapped into
 * `[0, 1440)`. Shifts that begin at or after `dayStart` get a small offset; shifts that begin
 * *before* `dayStart` (overnight shifts in AM-day-start departments) get pushed to the end of
 * the ordering — exactly where they belong in a chronological reading of the staffing day.
 *
 * Example for `dayStart="06:00"`:
 *  - `"07:00-15:00"` → 60   (7 AM, 1h after day start)
 *  - `"15:00-23:00"` → 540  (3 PM, 9h after day start)
 *  - `"23:00-07:00"` → 1020 (11 PM, 17h after day start — last)
 */
const shiftSortKey = (sheetKey: string, dayStartMinutes: number): number => {
	const match = sheetKey.match(/^(\d{2}):(\d{2})-/);
	if (!match) return Number.POSITIVE_INFINITY; // non-shift keys (e.g. sentinels) go to the end
	const startMin = Number(match[1]) * 60 + Number(match[2]);
	return (startMin - dayStartMinutes + MINUTES_IN_DAY) % MINUTES_IN_DAY;
};

/** Bucket a section's rows by their exact grouping-window key — the default tab-split behavior. */
const bucketRowsByExactKey = (rows: ScheduleRow[], groupingOption: DailyPrintoutGroupingOption, jobManagementEnabled: boolean): Map<string, ScheduleRow[]> => {
	const byKey = new Map<string, ScheduleRow[]>();
	for (const row of rows) {
		const sheetKey = groupKeyFor({ row, groupingOption, jobManagementEnabled });
		const existing = byKey.get(sheetKey);
		if (existing) existing.push(row);
		else byKey.set(sheetKey, [row]);
	}
	return byKey;
};

interface SplitSheetsArgs {
	sections: SectionPlan[];
	separateSheetsBy: DailyPrintoutGroupingOption;
	customSheetTabs: CustomSheetTabsConfig | undefined;
	department: Department;
	jobManagementEnabled: boolean;
	/** When on (and splitting by Shift Time), tabs snap to the department's popular shift windows. */
	alignGroupTimesToPopular?: boolean;
	shiftTimeAnchors?: ShiftTimeAnchor[];
	day?: DateTime;
	timezone?: string;
	/** When set, Start Time tabs are one per configured group instead of per shift-time window. */
	customTimeGroups: ActiveCustomTimeGroups | null;
	/** Time off and call-outs printed on the tabs; with custom groups, one covering no group needs the Other tab. */
	timeOffAndCallOuts: ReadonlyArray<TimeOffOrCallOut>;
}

/** The part of a time-off entry or call-out that decides which Start Time tab it prints on. */
interface TimeOffOrCallOut {
	startTime: DateTime;
	endTime: DateTime;
}

/**
 * Start Time tabs from custom groups: one tab per configured group, empty groups included, in the
 * groups' timeline order from the day start (Other last, when a staff row, time off or a call-out covers
 * no group — so nothing covering no group is left without a tab). A row covering
 * two groups prints on both tabs. Sections keep their order on each tab. An empty custom Start Time
 * section prints on its own group's tab only. Without Sections, an empty group's tab still prints the
 * empty table, the way an empty group column still prints its heading; with Sections it has only its
 * header (and any time off or call-outs that cover it).
 */
const splitByCustomTimeGroups = (
	sections: SectionPlan[],
	customTimeGroups: ActiveCustomTimeGroups,
	timeOffAndCallOuts: ReadonlyArray<TimeOffOrCallOut>,
): SheetPlan[] => {
	const { groups, timezone, dayStart, labels } = customTimeGroups;
	const bucketRows = (rows: ScheduleRow[]) => bucketRowsByCustomTimeGroup({ rows, groups, timezone, dayStart, sortDirection: "asc" });
	// Bucketing every row at once gives the tab list: each configured group, then Other when any staff row covers none.
	const rowSheetKeys = bucketRows(sections.flatMap((section) => section.rows)).map((bucket) => bucket.key);
	const otherNeededOffRoster =
		!rowSheetKeys.includes(UNMATCHED_GROUP_KEY) &&
		timeOffAndCallOuts.some((shift) =>
			shiftBelongsToCustomTimeGroupKey({ shiftStart: shift.startTime, shiftEnd: shift.endTime, key: UNMATCHED_GROUP_KEY, groups, timezone }),
		);
	const sheetKeys = otherNeededOffRoster ? [...rowSheetKeys, UNMATCHED_GROUP_KEY] : rowSheetKeys;
	const sheetSections = new Map<string, SectionPlan[]>(sheetKeys.map((key) => [key, []]));
	const sectionsOf = (sheetKey: string): SectionPlan[] => {
		const target = sheetSections.get(sheetKey);
		if (!target) throw new Error(`Custom time group tab missing for ${sheetKey}`);
		return target;
	};
	const ungrouped = sections.length === 1 && sections[0].groupingKey === UNGROUPED_SECTION_KEY;

	for (const section of sections) {
		if (section.rows.length === 0) {
			const groupKey = section.groupingKey.split(COMPOSITE_SECTION_KEY_SEPARATOR).find(isCustomTimeGroupKey);
			if (!groupKey) throw new Error(`Empty section without a custom time group: ${section.groupingKey}`);
			sectionsOf(groupKey).push(section);
			continue;
		}
		for (const bucket of bucketRows(section.rows)) {
			if (bucket.rows.length === 0 && !ungrouped) continue;
			sectionsOf(bucket.key).push({ groupingKey: section.groupingKey, rows: bucket.rows });
		}
	}
	const finalNames = finalizeUserSheetNames(sheetKeys.map((key) => customTimeGroupKeyLabel(key, labels)));
	return sheetKeys.map<SheetPlan>((rawKey, i) => ({
		sheetName: finalNames[i],
		rawSheetKey: rawKey,
		splitBy: DailyPrintoutGroupingOption.START_TIME,
		sections: sectionsOf(rawKey),
	}));
};

/**
 * Phase 4 — partition sorted sections into one or more sheets (mapping §4 phase 4 / §7.*).
 *
 * Three modes (§7.1):
 * - `NONE` — a single sheet containing all sections
 * - Standard grouping kind (`LOCATION` / `ROLE` / `FLOOR` / `START_TIME`) — re-bucket each section's
 *   rows by that key; one sheet per resulting bucket. For `START_TIME`, the sheets are also passed
 *   through overlap-merging (§7.2) and section dedup.
 * - `CUSTOM` — completely separate flow driven by `customSheetTabs` (§7.3)
 *
 * The output preserves section ordering within each sheet. Sheet names are finalized (dedup +
 * Excel sanitization) before the renderer sees them — sheet names never get reassigned later.
 */
export const splitSheets = (args: SplitSheetsArgs): SheetPlan[] => {
	const {
		sections,
		separateSheetsBy,
		customSheetTabs,
		department,
		jobManagementEnabled,
		alignGroupTimesToPopular,
		shiftTimeAnchors,
		day,
		timezone,
		customTimeGroups,
		timeOffAndCallOuts,
	} = args;

	// When the user aligns shift-time groups to the department's popular windows, the same anchors
	// drive the Shift Time tab split: each section's rows are bucketed into the popular anchor windows
	// (reusing the grouping-phase routine) instead of exact windows + overlap-merge.
	const useDepartmentAnchors =
		separateSheetsBy === DailyPrintoutGroupingOption.START_TIME && !!alignGroupTimesToPopular && !!shiftTimeAnchors?.length && !!day && !!timezone;

	// NONE mode — one sheet
	if (separateSheetsBy === DailyPrintoutGroupingOption.NONE) {
		return [
			{
				sheetName: "Daily Schedule",
				rawSheetKey: "all",
				splitBy: DailyPrintoutGroupingOption.NONE,
				sections,
			},
		];
	}

	const sortedEntries: Array<[string, ScheduleRow[]]> = sections.map((s) => [s.groupingKey, s.rows]);

	// CUSTOM mode — user-defined tabs
	if (separateSheetsBy === DailyPrintoutGroupingOption.CUSTOM) {
		const tabs = customSheetTabs?.tabs ?? [];
		const candidates = buildCustomTabCandidates({ tabs, sortedEntries, department, jobManagementEnabled });
		const finalNames = finalizeSheetNames(candidates.map((c) => c.sheetKey));

		return candidates.map<SheetPlan>((candidate, i) => ({
			sheetName: finalNames[i],
			rawSheetKey: candidate.sheetKey,
			splitBy: DailyPrintoutGroupingOption.CUSTOM,
			sections: candidate.entries.map<SectionPlan>(([groupingKey, rows]) => ({ groupingKey, rows })),
		}));
	}

	if (separateSheetsBy === DailyPrintoutGroupingOption.START_TIME && customTimeGroups) {
		return splitByCustomTimeGroups(sections, customTimeGroups, timeOffAndCallOuts);
	}

	// Standard split — re-bucket section rows by the chosen grouping kind, one sheet per bucket
	let sheetsGroupMap: SheetBucketMap = new Map();
	for (const [groupingKey, rows] of sortedEntries) {
		// Bucket this section's rows into sheet keys: popular anchor windows when aligning, otherwise
		// the exact `groupKeyFor` window.
		const rowsBySheetKey =
			useDepartmentAnchors && shiftTimeAnchors && day && timezone
				? groupRowsByDepartmentAnchors(rows, shiftTimeAnchors, day, timezone).groups
				: bucketRowsByExactKey(rows, separateSheetsBy, jobManagementEnabled);
		for (const [sheetKey, sheetRows] of rowsBySheetKey) {
			const existing = sheetsGroupMap.get(sheetKey);
			if (existing) existing.push([groupingKey, sheetRows]);
			else sheetsGroupMap.set(sheetKey, [[groupingKey, sheetRows]]);
		}
	}

	// START_TIME post-processing: merge overlapping shift windows, then dedup sections within each
	// merged sheet (mapping §7.2). Skipped when aligning to popular windows — the anchors already
	// define the canonical buckets, so overlap-merging would undo that. Other grouping kinds skip too.
	if (separateSheetsBy === DailyPrintoutGroupingOption.START_TIME && !useDepartmentAnchors) {
		sheetsGroupMap = mergeOverlappingShiftSheets(sheetsGroupMap);
		sheetsGroupMap = dedupSectionsAfterShiftMerge(sheetsGroupMap);
	}

	let sheetKeys = [...sheetsGroupMap.keys()];
	// For START_TIME splits, order tabs chronologically anchored to the department's `dayStart`,
	// so the first tab is the one starting closest to the staffing-day boundary. Example: a 6 AM
	// `dayStart` produces tab order [7a-3p, 3p-11p, 11p-7a] rather than insertion order.
	if (separateSheetsBy === DailyPrintoutGroupingOption.START_TIME) {
		const { hour: dsHour, minute: dsMinute } = parseTimeString(department.dayStart);
		const dayStartMinutes = dsHour * 60 + dsMinute;
		sheetKeys = [...sheetKeys].sort((a, b) => shiftSortKey(a, dayStartMinutes) - shiftSortKey(b, dayStartMinutes));
	}
	// Convert raw grouping keys to user-facing display names BEFORE dedupe/sanitize so the (N)
	// disambiguating suffixes attach to the formatted name, not the raw key.
	// For START_TIME, this turns "06:00-18:00" into "6a-6p" (mirrors legacy groupingKeyToDisplayName).
	// For ROLE under Job Management, the raw key is `nurse.jobId` (a UUID) — resolve it to the job's
	// display name via `groupingKeyToDisplayName`, the same resolver `renderSheets.ts` already uses
	// for the in-sheet header title, so the Excel tab name doesn't show the raw id.
	// Other split kinds (LOCATION, FLOOR, and ROLE without Job Management) pass through unchanged
	// because their raw key is already the user-facing string.
	const displayNames =
		separateSheetsBy === DailyPrintoutGroupingOption.START_TIME
			? sheetKeys.map(shiftWindowKeyToDisplayName)
			: separateSheetsBy === DailyPrintoutGroupingOption.ROLE && jobManagementEnabled
				? sheetKeys.map((key) => groupingKeyToDisplayName(key, separateSheetsBy, jobManagementEnabled, department))
				: sheetKeys;
	const finalNames = finalizeSheetNames(displayNames);
	return sheetKeys.map<SheetPlan>((rawKey, i) => ({
		sheetName: finalNames[i],
		rawSheetKey: rawKey,
		splitBy: separateSheetsBy,
		sections: (sheetsGroupMap.get(rawKey) ?? []).map<SectionPlan>(([groupingKey, rows]) => ({ groupingKey, rows })),
	}));
};
